// Installable web app + push review reminders.
// - registers /sw.js (no caching; it only handles install and pushes)
// - keeps the browser's install prompt so we can offer it at a good moment
// - subscribes this browser to "reviews ready" pushes and reports it to the backend
import { backendService } from './api/backendService';
import { getToken } from '../config/firebase';

let deferredInstallPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });

export function onPwaChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const isAndroid = () => /android/i.test(window.navigator.userAgent);

// Android has a real app now, so Android browsers are sent to Google Play
// instead of the web install prompt (7 of 7 web prompts were dismissed).
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.onikanji.app' +
  `&referrer=${encodeURIComponent('utm_source=onikanji_web&utm_medium=website&utm_content=reminder_card')}`;

export const canPromptInstall = () => Boolean(deferredInstallPrompt) && !isAndroid();

// Brave blocks push unless "Use Google services for push messaging" is on.
export async function isBrave() {
  try {
    return Boolean(navigator.brave && (await navigator.brave.isBrave()));
  } catch (e) {
    return false;
  }
}

async function api(path, body) {
  const token = getToken();
  if (!token) return null;
  const res = await fetch(`${backendService.baseURL}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { Authorization: `Bearer ${token}`, ...(body !== undefined && { 'Content-Type': 'application/json' }) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
  return res.json();
}

export function trackPwaEvent(type, meta) {
  api('/push/event', meta ? { type, meta } : { type }).catch(() => {});
}

export function registerPwa() {
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    trackPwaEvent('installed');
    notify();
  });
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((err) => console.warn('Service worker registration failed:', err));
  });
}

// Called once the learner is signed in: daily "opened as installed app" signal
// and open tracking for notification clicks (?push_id=…).
export function trackAppOpen() {
  if (isStandalone()) trackPwaEvent('standalone_open', { ios: isIos() });
  const params = new URLSearchParams(window.location.search);
  const pushId = Number(params.get('push_id'));
  if (pushId > 0) {
    api('/push/opened', { pushId }).catch(() => {});
    params.delete('push_id');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }
}

export async function promptInstall() {
  if (!deferredInstallPrompt) return 'unavailable';
  const prompt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  trackPwaEvent('install_prompt_shown');
  prompt.prompt();
  const { outcome } = await prompt.userChoice;
  trackPwaEvent(outcome === 'accepted' ? 'install_accepted' : 'install_dismissed');
  notify();
  return outcome;
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function currentSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

// 'on' | 'off' | 'blocked' | 'unsupported'
export async function getPushState() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  const sub = await currentSubscription();
  if (!sub || Notification.permission !== 'granted') return 'off';
  // The browser may still hold a subscription the backend revoked (or that
  // belongs to another account on this device): ask the backend.
  try {
    const status = await api(`/push/status?endpoint=${encodeURIComponent(sub.endpoint)}`);
    return status?.thisDevice ? 'on' : 'off';
  } catch (e) {
    return 'on';
  }
}

// Asks for permission (must be called from a click) and subscribes.
// Returns 'on' | 'denied' | 'dismissed' | 'unsupported' | 'error' | 'brave_blocked'.
export async function enablePush() {
  if (!pushSupported()) return 'unsupported';
  try {
    const keyRes = await fetch(`${backendService.baseURL}/push/public-key`);
    if (!keyRes.ok) return 'unsupported';
    const { publicKey } = await keyRes.json();

    trackPwaEvent('push_prompt_shown', { standalone: isStandalone() });
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      trackPwaEvent(permission === 'denied' ? 'push_denied' : 'push_dismissed');
      return permission === 'denied' ? 'denied' : 'dismissed';
    }
    trackPwaEvent('push_granted');

    const reg = (await navigator.serviceWorker.getRegistration('/')) || (await navigator.serviceWorker.register('/sw.js'));
    await navigator.serviceWorker.ready;
    const sub = (await reg.pushManager.getSubscription()) ||
      (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) }));
    await api('/push/subscribe', {
      subscription: sub.toJSON(),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      standalone: isStandalone(),
    });
    notify();
    return 'on';
  } catch (err) {
    console.error('Enabling push failed:', err);
    const brave = await isBrave();
    // Record why, so failures like "granted but never subscribed" are visible.
    trackPwaEvent('push_error', {
      name: String(err?.name || ''), message: String(err?.message || '').slice(0, 200),
      brave, standalone: isStandalone(), ua: navigator.userAgent.slice(0, 200),
    });
    return brave ? 'brave_blocked' : 'error';
  }
}

export async function disablePush() {
  const sub = await currentSubscription();
  if (sub) {
    await api('/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  notify();
}

export async function sendTestPush() {
  return api('/push/test', {});
}
